import React, { useState } from 'react';
import { 
  HelpCircle, ChevronDown, ChevronRight, Search,
  Home, MessageSquare, AlertTriangle, Users, Building2,
  Calendar, FileText, CreditCard, Settings, Shield,
  Video, Vote, UserPlus, Bell, Mail
} from 'lucide-react';

const Aide = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqSections = [
    {
      id: 'presentation',
      title: 'Présentation de SyndicAppli',
      icon: <Home className="text-[#00D3A8]" size={24} />,
      description: 'Découvrez les fonctionnalités principales de la plateforme',
      questions: [
        {
          q: "Qu'est-ce que SyndicAppli ?",
          a: "SyndicAppli est une plateforme complète de gestion de copropriété. Elle permet aux gestionnaires, coproprietaires et prestataires de collaborer efficacement pour la gestion des immeubles, des assemblées générales, des incidents et de la communication."
        },
        {
          q: "Quels sont les différents rôles dans l'application ?",
          a: `L'application propose plusieurs rôles :\n\n• **Gestionnaire** : Administre les immeubles, organise les AG, gère les incidents et les prestataires\n• **Copropriétaire** : Participe aux AG, vote, déclare des incidents, communique avec le gestionnaire\n• **Prestataire** : Intervient sur les incidents qui lui sont assignés\n• **Sous-compte Locataire** : Accès limité pour consulter/créer des incidents\n• **Sous-compte Mandataire** : Peut voter en AG au nom d'un coproprietaire`
        },
        {
          q: "Comment naviguer dans l'application ?",
          a: "Le menu latéral gauche vous donne accès à toutes les fonctionnalités :\n\n• **Accueil** : Tableau de bord avec vue d'ensemble\n• **Messagerie** : Communication en temps réel\n• **Incidents** : Déclaration et suivi des problèmes\n• **Assemblées** : Gestion des AG\n• **Mes Immeubles/Copropriétaires** : Gestion des biens\n• **Événements/Annonces** : Actualités de la copropriété"
        }
      ]
    },
    {
      id: 'assemblees',
      title: 'Assemblées Générales',
      icon: <Users className="text-[#00D3A8]" size={24} />,
      description: 'Tout savoir sur les AG en visioconférence',
      questions: [
        {
          q: "Comment créer une Assemblée Générale ?",
          a: "En tant que gestionnaire :\n\n1. Allez dans **Assemblées**\n2. Cliquez sur **Nouvelle AG**\n3. Remplissez les informations (titre, date, lieu, ordre du jour)\n4. Ajoutez les résolutions à voter\n5. Publiez l'AG pour notifier les coproprietaires"
        },
        {
          q: "Comment participer à une AG en visioconférence ?",
          a: "1. Accédez à l'AG depuis votre tableau de bord ou le menu Assemblées\n2. Confirmez votre mode de participation (vidéo, audio, ou procuration)\n3. Quand l'AG est ouverte, la visioconférence démarre automatiquement\n4. Vous pouvez lever la main, utiliser le chat, et voter sur les résolutions"
        },
        {
          q: "Comment fonctionne le vote ?",
          a: "Pendant l'AG :\n\n• Le gestionnaire ouvre le vote sur une résolution\n• Les boutons **Pour**, **Contre**, **Abstention** apparaissent\n• Cliquez sur votre choix\n• Le vote est pondéré selon vos tantièmes\n• Les résultats s'affichent en temps réel"
        },
        {
          q: "Comment donner procuration ?",
          a: "Si vous ne pouvez pas assister à l'AG :\n\n1. Ouvrez les détails de l'AG\n2. Cliquez sur **Donner procuration**\n3. Entrez les coordonnées du mandataire\n4. Un pouvoir PDF est généré et envoyé pour signature\n5. Le mandataire pourra voter en votre nom"
        },
        {
          q: "Qu'est-ce que le Bureau de l'AG ?",
          a: "Le Bureau est constitué de :\n\n• **Président de séance** : Dirige les débats\n• **Secrétaire** : Rédige le procès-verbal\n• **Scrutateur(s)** : Vérifie le décompte des votes\n\nLe gestionnaire peut désigner ces rôles avant d'ouvrir l'AG."
        },
        {
          q: "Comment est généré le procès-verbal ?",
          a: "À la clôture de l'AG :\n\n• Un PV Word est généré automatiquement\n• Il contient : participants, résolutions, votes, résultats\n• Le tableau des connexions/déconnexions est inclus\n• Le document est téléchargeable dans l'onglet Documents"
        }
      ]
    },
    {
      id: 'incidents',
      title: 'Gestion des Incidents',
      icon: <AlertTriangle className="text-[#00D3A8]" size={24} />,
      description: 'Déclarer et suivre les problèmes',
      questions: [
        {
          q: "Comment déclarer un incident ?",
          a: "1. Allez dans **Incidents**\n2. Cliquez sur **Nouvel incident**\n3. Remplissez :\n   • Titre et description\n   • Catégorie (plomberie, électricité, etc.)\n   • Niveau d'urgence\n   • Photos si nécessaire\n4. Validez la déclaration"
        },
        {
          q: "Quels sont les niveaux d'urgence ?",
          a: "• **Faible** : Peut attendre, pas de danger immédiat\n• **Moyen** : À traiter sous quelques jours\n• **Élevé** : Urgent, nécessite une intervention rapide (fuite d'eau, panne ascenseur...)"
        },
        {
          q: "Comment suivre l'avancement d'un incident ?",
          a: "Dans la liste des incidents :\n\n• **En attente** : Déclaré, pas encore pris en charge\n• **En cours** : Un prestataire est assigné et travaille dessus\n• **Résolu** : L'intervention est terminée\n\nCliquez sur un incident pour voir l'historique complet."
        },
        {
          q: "Comment assigner un prestataire ?",
          a: "En tant que gestionnaire :\n\n1. Ouvrez l'incident\n2. Cliquez sur **Assigner un prestataire**\n3. Sélectionnez dans la liste des prestataires\n4. Le prestataire est notifié automatiquement"
        }
      ]
    },
    {
      id: 'messagerie',
      title: 'Messagerie',
      icon: <MessageSquare className="text-[#00D3A8]" size={24} />,
      description: 'Communication en temps réel',
      questions: [
        {
          q: "Comment envoyer un message ?",
          a: "1. Allez dans **Messagerie**\n2. Sélectionnez une conversation existante ou créez-en une\n3. Tapez votre message et appuyez sur Entrée\n4. Les messages sont envoyés en temps réel"
        },
        {
          q: "Comment créer une conversation de groupe ?",
          a: "1. Dans Messagerie, cliquez sur **Nouvelle conversation**\n2. Sélectionnez **Groupe**\n3. Choisissez les participants\n4. Donnez un nom au groupe\n5. La conversation est créée"
        },
        {
          q: "Comment recevoir des notifications ?",
          a: "Les notifications apparaissent :\n\n• En badge sur l'icône de messagerie\n• Dans le panneau de notifications (cloche)\n• Par notification navigateur si autorisé"
        }
      ]
    },
    {
      id: 'immeubles',
      title: 'Gestion des Immeubles',
      icon: <Building2 className="text-[#00D3A8]" size={24} />,
      description: 'Administration des bâtiments',
      questions: [
        {
          q: "Comment ajouter un immeuble ?",
          a: "En tant que gestionnaire :\n\n1. Allez dans **Mes Immeubles**\n2. Cliquez sur **Nouvel immeuble**\n3. Remplissez les informations (nom, adresse, nombre de lots)\n4. Validez la création"
        },
        {
          q: "Comment gérer les coproprietaires ?",
          a: "1. Allez dans **Mes Copropriétaires**\n2. Vous pouvez :\n   • Ajouter un coproprietaire manuellement\n   • Importer via CSV\n   • Modifier les informations\n   • Gérer les tantièmes"
        },
        {
          q: "Comment importer/exporter des données ?",
          a: "Sur les pages de gestion (Copropriétaires, Équipe, Prestataires) :\n\n• **Exporter CSV** : Télécharge la liste actuelle\n• **Importer CSV** : Charge des données depuis un fichier\n\nLe format CSV doit respecter les colonnes attendues."
        }
      ]
    },
    {
      id: 'compte',
      title: 'Mon Compte',
      icon: <Settings className="text-[#00D3A8]" size={24} />,
      description: 'Paramètres et profil',
      questions: [
        {
          q: "Comment modifier mes informations ?",
          a: "1. Cliquez sur votre nom en haut à droite\n2. Sélectionnez **Mon compte**\n3. Modifiez vos informations (nom, email, téléphone)\n4. Changez votre photo de profil\n5. Enregistrez les modifications"
        },
        {
          q: "Comment changer mon mot de passe ?",
          a: "1. Allez dans **Mon compte**\n2. Section **Sécurité**\n3. Entrez votre ancien mot de passe\n4. Entrez le nouveau mot de passe (2 fois)\n5. Validez le changement"
        },
        {
          q: "J'ai oublié mon mot de passe",
          a: "1. Sur la page de connexion, cliquez sur **J'ai oublié mon mot de passe**\n2. Entrez votre adresse email\n3. Vous recevrez un lien de réinitialisation\n4. Le lien est valable 1 heure"
        }
      ]
    },
    {
      id: 'sous-comptes',
      title: 'Sous-comptes',
      icon: <UserPlus className="text-[#00D3A8]" size={24} />,
      description: 'Gérer les accès locataires et mandataires',
      questions: [
        {
          q: "Qu'est-ce qu'un sous-compte ?",
          a: "Un sous-compte permet à un coproprietaire de donner un accès limité à :\n\n• **Locataire** : Peut consulter et créer des incidents\n• **Mandataire** : Peut participer aux AG et voter"
        },
        {
          q: "Comment créer un sous-compte ?",
          a: "1. Allez dans **Sous-comptes** (menu coproprietaire)\n2. Cliquez sur **Nouveau sous-compte**\n3. Remplissez les informations (nom, email, type)\n4. Définissez les permissions\n5. Une invitation est envoyée par email"
        },
        {
          q: "Comment le sous-compte active son accès ?",
          a: "1. Le sous-compte reçoit un email d'invitation\n2. Il clique sur le lien d'activation\n3. Il définit son mot de passe\n4. Son compte est activé et prêt à utiliser"
        }
      ]
    },
    {
      id: 'modules',
      title: 'Modules & Paiements',
      icon: <CreditCard className="text-[#00D3A8]" size={24} />,
      description: 'Fonctionnalités additionnelles',
      questions: [
        {
          q: "Comment activer un module payant ?",
          a: "1. Allez dans **Modules**\n2. Parcourez les modules disponibles\n3. Cliquez sur **Activer** sur le module souhaité\n4. Procédez au paiement sécurisé via Stripe"
        },
        {
          q: "Quels moyens de paiement sont acceptés ?",
          a: "Nous acceptons :\n\n• Cartes bancaires (Visa, Mastercard, American Express)\n• Paiement sécurisé via Stripe"
        },
        {
          q: "Comment annuler un abonnement ?",
          a: "1. Allez dans **Modules** > **Mes abonnements**\n2. Sélectionnez l'abonnement à annuler\n3. Cliquez sur **Annuler**\n4. L'accès reste actif jusqu'à la fin de la période payée"
        }
      ]
    }
  ];

  // Filter sections based on search
  const filteredSections = searchQuery 
    ? faqSections.map(section => ({
        ...section,
        questions: section.questions.filter(q => 
          q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.questions.length > 0)
    : faqSections;

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
    setExpandedQuestion(null);
  };

  const toggleQuestion = (questionIndex) => {
    setExpandedQuestion(expandedQuestion === questionIndex ? null : questionIndex);
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00D3A8] to-[#0099CC] rounded-2xl p-6 md:p-8 text-white mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <HelpCircle size={32} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Centre d'aide</h1>
            <p className="text-white/80">Guide complet de SyndicAppli</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="relative mt-4">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Rechercher dans l'aide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
          />
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: <Users size={24} />, label: 'Assemblées', section: 'assemblees' },
          { icon: <AlertTriangle size={24} />, label: 'Incidents', section: 'incidents' },
          { icon: <MessageSquare size={24} />, label: 'Messagerie', section: 'messagerie' },
          { icon: <Settings size={24} />, label: 'Mon Compte', section: 'compte' }
        ].map((item, i) => (
          <button
            key={i}
            onClick={() => {
              setExpandedSection(item.section);
              document.getElementById(item.section)?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="flex flex-col items-center p-4 bg-white rounded-xl border border-gray-100 hover:border-[#00D3A8] hover:shadow-md transition-all"
          >
            <div className="text-[#00D3A8] mb-2">{item.icon}</div>
            <span className="text-sm font-medium text-gray-700">{item.label}</span>
          </button>
        ))}
      </div>

      {/* FAQ Sections */}
      <div className="space-y-4">
        {filteredSections.map((section) => (
          <div 
            key={section.id} 
            id={section.id}
            className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          >
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#00D3A8]/10 rounded-xl flex items-center justify-center">
                  {section.icon}
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-gray-800">{section.title}</h2>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <ChevronDown 
                className={`text-gray-400 transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`} 
                size={24} 
              />
            </button>

            {/* Questions */}
            {expandedSection === section.id && (
              <div className="border-t border-gray-100">
                {section.questions.map((qa, qIndex) => (
                  <div key={qIndex} className="border-b border-gray-50 last:border-b-0">
                    <button
                      onClick={() => toggleQuestion(`${section.id}-${qIndex}`)}
                      className="w-full flex items-center justify-between p-4 pl-8 hover:bg-gray-50 transition-colors text-left"
                    >
                      <span className="font-medium text-gray-700 pr-4">{qa.q}</span>
                      <ChevronRight 
                        className={`text-gray-400 flex-shrink-0 transition-transform ${expandedQuestion === `${section.id}-${qIndex}` ? 'rotate-90' : ''}`} 
                        size={20} 
                      />
                    </button>
                    {expandedQuestion === `${section.id}-${qIndex}` && (
                      <div className="px-8 pb-4">
                        <div className="bg-gray-50 rounded-lg p-4 text-gray-600 text-sm whitespace-pre-line">
                          {qa.a.split('\n').map((line, i) => {
                            // Bold text between **
                            const parts = line.split(/\*\*(.*?)\*\*/g);
                            return (
                              <p key={i} className="mb-1 last:mb-0">
                                {parts.map((part, j) => 
                                  j % 2 === 1 ? <strong key={j}>{part}</strong> : part
                                )}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact Support */}
      <div className="mt-8 bg-gray-50 rounded-xl p-6 text-center">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Vous n'avez pas trouvé votre réponse ?
        </h3>
        <p className="text-gray-600 mb-4">
          Notre assistant IA peut vous aider avec des questions plus spécifiques.
        </p>
        <p className="text-sm text-gray-500">
          Cliquez sur le bouton <span className="inline-flex items-center px-2 py-1 bg-[#00D3A8] text-white rounded-full text-xs"><HelpCircle size={12} className="mr-1" /> ?</span> en bas à droite pour discuter avec l'assistant.
        </p>
      </div>
    </div>
  );
};

export default Aide;
